import { Modal, Form, Select, Input, message } from 'antd';
import AxiosOICStat from "AxiosOICStat";
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

const { Option } = Select;

const FaoIndicatorMappingModal = ({ visible, onCancel, onSuccess, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const indicatorsState = useSelector((state) => state.indicators);

  const availableIndicators = indicatorsState.value
    ? Object.values(indicatorsState.value).sort((a, b) => (a.ind_name_eng || '').localeCompare(b.ind_name_eng || ''))
    : [];

  useEffect(() => {
    if (visible) fetchMappings();
  }, [visible]);

  const fetchMappings = async () => {
    try {
      const res = await AxiosOICStat.get('/external_api/fao/indicator-mappings');
      setMappings(res.data?.mappings || res.data || []);
    } catch (e) {
      // ignore
    }
  };

  const [faoItems, setFaoItems] = useState([]);
  const [faoSearch, setFaoSearch] = useState('');
  const searchTimer = useRef(null);

  useEffect(() => {
    if (visible && record) {
      const composite = `${record.fao_item_code || ''}|${record.fao_element_code || ''}`;
      form.setFieldsValue({
        oic_ind_code: record.oic_ind_code ? String(record.oic_ind_code) : undefined,
        oic_ind_name: record.oic_ind_name,
        fao_indicator_code: composite,
        fao_item_code: record.fao_item_code || null,
        fao_element_code: record.fao_element_code || null,
        fao_item_name: record.fao_item_name || null,
        fao_element_name: record.fao_element_name || null,
        fao_domain: record.fao_domain || null,
      });

      const exists = faoItems.some(i => (`${i.fao_item_code || ''}|${i.fao_element_code || ''}`) === composite);
      if (!exists) {
        const toPrepend = {
          fao_item_code: record.fao_item_code || null,
          fao_element_code: record.fao_element_code || null,
          fao_item_name: record.fao_item_name || null,
          fao_element_name: record.fao_element_name || null,
          fao_domain: record.fao_domain || null,
        };
        setFaoItems(prev => [toPrepend, ...prev]);
      }
    }
    if (!visible && !record) form.resetFields();
  }, [visible, record, form, faoItems]);

  useEffect(() => {
    if (visible) {
      fetchMappings();
      fetchFaoItems('');
      setFaoSearch('');
    }
  }, [visible]);

  const fetchFaoItems = async (search = '') => {
    try {
      const q = [];
      if (typeof search === 'string' && search.trim()) q.push(`search=${encodeURIComponent(search.trim())}`);
      q.push('limit=100');
      const url = `/external_api/fao/get-all-fao-data?${q.join('&')}`;
      const res = await AxiosOICStat.get(url);
      const items = res.data || res.data?.items || [];
      setFaoItems(Array.isArray(items) ? items : []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchFaoItems(faoSearch || '');
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [faoSearch]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (record && record.id) {
        await AxiosOICStat.put(`/external_api/fao/indicator-mappings/${record.id}`, values);
        message.success('Indicator mapping updated');
      } else {
        await AxiosOICStat.post('/external_api/fao/indicator-mappings', values);
        message.success('Indicator mapping created');
      }
      onSuccess();
    } catch (err) {
      if (err.errorFields) return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save mapping';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const validateUnique = (field, val) => {
    if (!val) return true;
    if (field === 'fao_indicator_code') {
      return !mappings.some(m => `${m.fao_item_code || ''}|${m.fao_element_code || ''}` === String(val) && (!record || String(m.id) !== String(record.id)));
    }
    return !mappings.some(m => String(m[field] || '') === String(val) && (!record || String(m.id) !== String(record.id)));
  };

  return (
    <Modal
      title={record ? 'Edit FAO Indicator Mapping' : 'Add FAO Indicator Mapping'}
      visible={visible}
      onOk={handleSubmit}
      onCancel={() => { form.resetFields(); onCancel(); }}
      confirmLoading={loading}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="oic_ind_code" label="OIC Indicator" rules={[{ required: true, message: 'Please select an OIC indicator' }]}>
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select an indicator"
            onChange={(val) => {
              const sel = availableIndicators.find(i => String(i.ind_code) === String(val));
              if (sel) form.setFieldsValue({ oic_ind_name: sel.ind_name_eng });
            }}
            optionFilterProp="children"
          >
            {availableIndicators.map(ind => (
              <Option key={String(ind.ind_code)} value={String(ind.ind_code)}>{ind.ind_code} - {ind.ind_name_eng}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="fao_indicator_code"
          label="FAO Indicator"
          rules={[{ required: true, message: 'Please select a FAO indicator' }, { validator: (_, val) => {
            if (!val) return Promise.resolve();
            if (!validateUnique('fao_indicator_code', val)) return Promise.reject(new Error('FAO indicator code already mapped'));
            return Promise.resolve();
          } }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select a FAO indicator"
            optionFilterProp="children"
            filterOption={false}
            onSearch={(val) => setFaoSearch(val)}
            onChange={(val) => {
              // val is composite: item|element
              const sel = faoItems.find(i => (`${i.fao_item_code || ''}|${i.fao_element_code || ''}`) === String(val));
              if (sel) {
                form.setFieldsValue({
                  fao_indicator_code: `${sel.fao_item_code || ''}|${sel.fao_element_code || ''}`,
                  fao_item_code: sel.fao_item_code || null,
                  fao_element_code: sel.fao_element_code || null,
                  fao_item_name: sel.fao_item_name || null,
                  fao_element_name: sel.fao_element_name || null,
                  fao_domain: sel.fao_domain || null,
                });
              }
            }}
          >
            {faoItems.map(it => (
              <Option key={`${it.fao_item_code || ''}|${it.fao_element_code || ''}`} value={`${it.fao_item_code || ''}|${it.fao_element_code || ''}`}>
                {`${it.fao_item_code || ''}${it.fao_element_code ? ' / ' + it.fao_element_code : ''} - ${it.fao_item_name || ''}${it.fao_element_name ? ' / ' + it.fao_element_name : ''} (${it.fao_domain || ''})`}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="fao_item_code" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="fao_item_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="fao_element_code" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="fao_element_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="fao_domain" style={{ display: 'none' }}>
          <Input />
        </Form.Item>


        <Form.Item name="oic_ind_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FaoIndicatorMappingModal;
